/**
 * PaymentHistorySection
 *
 * Displays the immutable payment ledger for an invoice. Each event is a
 * row from invoice_payments. Voiding inserts a new reversal row with a
 * negative amount and paymentMethod=reversal (preserving the audit trail).
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  openReceiptPdf,
  printReceiptPdf,
  useVoidInvoicePayment,
  type InvoicePaymentEntry,
} from '@/hooks/useInvoices';
import { api } from '@/lib/api';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { translateBillingError } from '@/lib/billingErrors';
import { formatDate } from '@/utils/locale';
import {
  sveaMethodCodeFromNote,
  sveaPaymentMethodKind,
  sveaPaymentMethodLabelKey,
} from '@/utils/sveaPaymentMethod';

const REVERSAL_PAYMENT_METHOD = 'reversal';
const VOIDED_PAYMENT_REF_PREFIX = 'voided:';

interface PaymentLedgerContextValue {
  invoiceId?: string;
  workOrderId?: string;
}

const PaymentLedgerContext = createContext<PaymentLedgerContextValue>({});

export function PaymentLedgerProvider({
  value,
  children,
}: {
  value: PaymentLedgerContextValue;
  children: ReactNode;
}) {
  return <PaymentLedgerContext.Provider value={value}>{children}</PaymentLedgerContext.Provider>;
}

function isReversalPayment(payment: InvoicePaymentEntry): boolean {
  return payment.paymentMethod === REVERSAL_PAYMENT_METHOD;
}

/**
 * Online rows with a known Svea code show that method. The note is only the
 * `svea_method:<code>` tag in that case, so it is not repeated under the label.
 * Reversal rows keep their own label and note.
 */
function paymentMethodPresentation(
  payment: InvoicePaymentEntry,
  t: (key: string) => string,
  tc: (key: string) => string,
): { label: string; hideNote: boolean } {
  if (isReversalPayment(payment)) return { label: t('payments.reversal'), hideNote: false };
  if (payment.paymentMethod === 'online') {
    const kind = sveaPaymentMethodKind(sveaMethodCodeFromNote(payment.note));
    if (kind) return { label: tc(sveaPaymentMethodLabelKey(kind)), hideNote: true };
  }
  return { label: tc(`paymentMethod.${payment.paymentMethod}`), hideNote: false };
}

function voidedPaymentIds(payments: InvoicePaymentEntry[]): Set<string> {
  const ids = new Set<string>();
  for (const payment of payments) {
    if (!isReversalPayment(payment) || !payment.paymentReference?.startsWith(VOIDED_PAYMENT_REF_PREFIX)) {
      continue;
    }
    const originalId = payment.paymentReference.slice(VOIDED_PAYMENT_REF_PREFIX.length);
    if (originalId.length > 0) ids.add(originalId);
  }
  return ids;
}


interface PaymentHistorySectionProps {
  payments: InvoicePaymentEntry[];
  remainingCents: number;
  invoiceId?: string;
  workOrderId?: string;
}

export function PaymentHistorySection({
  payments,
  remainingCents,
  invoiceId: invoiceIdProp,
  workOrderId: workOrderIdProp,
}: PaymentHistorySectionProps) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const ledgerCtx = useContext(PaymentLedgerContext);
  const invoiceId = invoiceIdProp ?? ledgerCtx.invoiceId ?? payments.find((p) => p.invoiceId)?.invoiceId;
  const workOrderId = workOrderIdProp ?? ledgerCtx.workOrderId;
  const canManageInvoices = useAuthStore((s) => s.hasAnyPermission(['invoices.manage']));
  const voidPayment = useVoidInvoicePayment();
  const [pendingVoid, setPendingVoid] = useState<InvoicePaymentEntry | null>(null);
  const [retryingReceiptId, setRetryingReceiptId] = useState<string | null>(null);
  const [retriedReceiptIds, setRetriedReceiptIds] = useState<Set<string>>(() => new Set());
  const voidedIds = voidedPaymentIds(payments);

  const receiptEntries = Array.from(new Map(
    payments
      .filter((payment) => payment.receiptId && payment.receiptNumber)
      .map((payment) => [payment.receiptId as string, payment]),
  ).values());

  const handleOpenReceipt = async (receiptId: string): Promise<void> => {
    try {
      await openReceiptPdf(receiptId);
    } catch {
      notify.error(t('payments.openReceiptFailed'));
    }
  };

  const handlePrintReceipt = async (receiptId: string): Promise<void> => {
    try {
      const printed = await printReceiptPdf(receiptId);
      if (printed.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
    } catch {
      notify.error(t('payments.openReceiptFailed'));
    }
  };

  const handleConfirmVoid = async (): Promise<void> => {
    if (!pendingVoid || !invoiceId) return;
    try {
      await voidPayment.mutateAsync({
        invoiceId,
        paymentId: pendingVoid.id,
        ...(workOrderId !== undefined ? { workOrderId } : {}),
        note: t('payments.voidDefaultReason'),
      });
      notify.success(t('payments.voidSuccess'));
      setPendingVoid(null);
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleRetry = async (receiptId: string): Promise<void> => {
    setRetryingReceiptId(receiptId);
    try {
      await api.post(`/receipts/${receiptId}/email/retry`, {});
      setRetriedReceiptIds((current) => new Set(current).add(receiptId));
      notify.success(t('payments.retryQueued'));
    } catch {
      notify.error(t('payments.retryFailed'));
    } finally {
      setRetryingReceiptId(null);
    }
  };

  if (payments.length === 0 && remainingCents <= 0) {
    return null;
  }

  return (
    <Box data-testid="payment-history-section" sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('payments.historyTitle')}
      </Typography>

      {receiptEntries.length > 0 ? (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {receiptEntries.map((receipt) => {
            const receiptId = receipt.receiptId as string;
            // No address means no email was ever queued (#3348); say so instead of "queued".
            const deliveryStatus = !receipt.receiptEmail
              ? 'not_requested'
              : retriedReceiptIds.has(receiptId)
                ? 'queued'
                : (receipt.receiptDeliveryStatus ?? 'queued');
            return (
              <Box
                key={receiptId}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>{receipt.receiptNumber}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {receipt.receiptEmail ?? t('payments.noEmail')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Chip
                    size="small"
                    label={t(`payments.deliveryStatus.${deliveryStatus}`)}
                    color={deliveryStatus === 'sent' ? 'success' : deliveryStatus === 'failed' ? 'error' : 'default'}
                  />
                  {deliveryStatus === 'failed' ? (
                    <Button
                      size="small"
                      startIcon={<ReplayIcon />}
                      disabled={retryingReceiptId === receiptId}
                      onClick={() => void handleRetry(receiptId)}
                    >
                      {t('payments.retryEmail')}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    startIcon={<PrintIcon />}
                    data-testid="print-receipt-button"
                    onClick={() => void handlePrintReceipt(receiptId)}
                  >
                    {t('actions.printReceipt')}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ReceiptLongIcon />}
                    onClick={() => void handleOpenReceipt(receiptId)}
                  >
                    {t('payments.openReceipt')}
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}

      {payments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('payments.noPayments')}
        </Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {payments.map((p) => {
            const date = formatDate(p.paidAt);
            const reversal = isReversalPayment(p);
            const voided = voidedIds.has(p.id);
            const { label: methodLabel, hideNote } = paymentMethodPresentation(p, t, tc);
            const showReference = Boolean(p.paymentReference) && !p.paymentReference?.startsWith(VOIDED_PAYMENT_REF_PREFIX);
            const canVoid = Boolean(invoiceId) && canManageInvoices && !reversal && !voided;
            return (
              <Box
                key={p.id}
                data-testid="payment-history-row"
                data-payment-kind={reversal ? 'reversal' : voided ? 'voided' : 'payment'}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 160px' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">
                      {date} · {methodLabel}
                    </Typography>
                    {voided ? (
                      <Chip size="small" color="warning" label={t('payments.voided')} />
                    ) : null}
                    {reversal ? (
                      <Chip size="small" color="error" label={t('payments.reversal')} />
                    ) : null}
                  </Stack>
                  {showReference ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('payments.reference')}: {p.paymentReference}
                    </Typography>
                  ) : null}
                  {p.note && !hideNote ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {p.note}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={p.amountCents < 0 ? 'error.main' : 'success.main'}
                  >
                    {formatPrice(p.amountCents)}
                  </Typography>
                  {canVoid ? (
                    <Button
                      size="small"
                      color="error"
                      data-testid="void-payment-button"
                      onClick={() => setPendingVoid(p)}
                    >
                      {t('payments.void')}
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {remainingCents > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {t('payments.remainingBalance')}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="error.main">
            {formatPrice(remainingCents)}
          </Typography>
        </Box>
      ) : null}

      <ConfirmDialog
        open={pendingVoid !== null}
        title={t('payments.voidTitle')}
        message={t('payments.voidConfirm', { amount: pendingVoid ? formatPrice(pendingVoid.amountCents) : '' })}
        confirmText={t('payments.void')}
        confirmColor="error"
        onConfirm={() => void handleConfirmVoid()}
        onCancel={() => setPendingVoid(null)}
        loading={voidPayment.isPending}
      />
    </Box>
  );
}
